/**
 * Mirror (Vectorworks-style): reflect a selection across an axis through two points.
 *
 * Pure maths over Excalidraw element JSON, so it is unit-testable without the editor:
 *  - box elements (rectangle, ellipse, diamond, frame, embeddable, iframe, text, image) reflect their
 *    centre; their angle becomes 2φ − θ. Symmetric shapes, text and images are kept "readable": an angle
 *    past ±90° is turned by 180° (a rectangle looks the same; text stays upright; an image swaps which way
 *    it is flipped). Images are mirrored for real through Excalidraw's `scale` [sx, sy].
 *  - lines, arrows, freedraw (incl. polygon fixture symbols) reflect every point; x / y is the first point,
 *    so it is recomputed from the reflected points (angle 0, points relative to the first).
 *  - bound text follows its container; arrows inside the selection re-bind to the mirrored copies
 *    (focus negated, fixedPoint mapped into the mirrored frame); bindings to elements outside the
 *    selection are kept only while the end still touches them.
 *  - a selected frame takes its children with it; a duplicated frame is named "… (mirrored)".
 *
 * Duplicate mode returns new elements (fresh ids, seeds, groups; fixtures re-dated so they count);
 * in-place mode returns the updated originals (plus outside arrows / targets whose bindings changed).
 */

export type Pt = [number, number];
export interface Axis {
  p: Pt;
  q: Pt;
}
// Excalidraw element JSON, loosely typed (the board works with plain objects).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type MEl = Record<string, any> & { id: string; type: string; x: number; y: number; width: number; height: number };

const LINEAR = new Set(['line', 'arrow', 'freedraw']);
const READABLE = new Set(['rectangle', 'ellipse', 'diamond', 'frame', 'magicframe', 'embeddable', 'iframe', 'text', 'image']);

const TAU = Math.PI * 2;
/** Angle into (−π, π]. */
export function normAngle(a: number): number {
  let r = a % TAU;
  if (r <= -Math.PI) r += TAU;
  if (r > Math.PI) r -= TAU;
  return Math.abs(r) < 1e-12 ? 0 : r;
}
/** Excalidraw stores angles in [0, 2π). */
const toStored = (a: number) => {
  const r = normAngle(a);
  return r < 0 ? r + TAU : r;
};

export function axisAngle(a: Axis): number {
  return Math.atan2(a.q[1] - a.p[1], a.q[0] - a.p[0]);
}

/** Axis from two clicks; null when they are (almost) the same point. */
export function axisFrom(p: Pt, q: Pt): Axis | null {
  return Math.hypot(q[0] - p[0], q[1] - p[1]) < 1e-6 ? null : { p, q };
}
/** Alt-click: a vertical axis through one point. */
export const verticalAxis = (p: Pt): Axis => ({ p, q: [p[0], p[1] + 1] });

export function reflectPoint(pt: Pt, a: Axis): Pt {
  const [px, py] = a.p;
  const dx = a.q[0] - px;
  const dy = a.q[1] - py;
  const len2 = dx * dx + dy * dy;
  const t = ((pt[0] - px) * dx + (pt[1] - py) * dy) / len2;
  const fx = px + t * dx;
  const fy = py + t * dy;
  return [2 * fx - pt[0], 2 * fy - pt[1]];
}

/** Reflected angle 2φ − θ, plus the readable variant (|angle| ≤ 90°, `rot180` when a half turn was added). */
export function mirrorAngle(theta: number, phi: number): { raw: number; angle: number; rot180: boolean } {
  const raw = normAngle(2 * phi - theta);
  if (Math.abs(raw) > Math.PI / 2 + 1e-9) return { raw, angle: normAngle(raw - Math.PI), rot180: true };
  return { raw, angle: raw, rot180: false };
}

const rotate = (p: Pt, c: Pt, a: number): Pt => {
  if (!a) return [p[0], p[1]];
  const s = Math.sin(a);
  const k = Math.cos(a);
  const dx = p[0] - c[0];
  const dy = p[1] - c[1];
  return [c[0] + dx * k - dy * s, c[1] + dx * s + dy * k];
};

/** Absolute points of a line / arrow / freedraw (rotation about the centre of its points box, as Excalidraw does). */
export function linearPoints(el: MEl): Pt[] {
  const pts: Pt[] = (el.points ?? [[0, 0]]).map((p: number[]) => [el.x + p[0], el.y + p[1]] as Pt);
  const a = el.angle ?? 0;
  if (!a) return pts;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const c: Pt = [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
  return pts.map((p) => rotate(p, c, a));
}

/** Outline in scene space (ghost preview): rotated box corners, an ellipse polygon, or a line's points. */
export function outlineOf(el: MEl): { pts: Pt[]; closed: boolean } {
  if (LINEAR.has(el.type)) return { pts: linearPoints(el), closed: false };
  const c: Pt = [el.x + el.width / 2, el.y + el.height / 2];
  const a = el.angle ?? 0;
  let local: Pt[];
  if (el.type === 'ellipse') {
    local = Array.from({ length: 24 }, (_, i) => {
      const t = (i / 24) * TAU;
      return [c[0] + (el.width / 2) * Math.cos(t), c[1] + (el.height / 2) * Math.sin(t)] as Pt;
    });
  } else if (el.type === 'diamond') {
    local = [
      [c[0], el.y],
      [el.x + el.width, c[1]],
      [c[0], el.y + el.height],
      [el.x, c[1]],
    ];
  } else {
    local = [
      [el.x, el.y],
      [el.x + el.width, el.y],
      [el.x + el.width, el.y + el.height],
      [el.x, el.y + el.height],
    ];
  }
  return { pts: local.map((p) => rotate(p, c, a)), closed: true };
}

/** How a point given as a [0..1] ratio of the element's box (Excalidraw `fixedPoint`) maps after mirroring. */
export type FixedMap = (fp: [number, number]) => [number, number];
const identityFixed: FixedMap = (fp) => [fp[0], fp[1]];

export interface Mirrored {
  patch: Partial<MEl>;
  fixed: FixedMap;
}

/** Geometry patch for one element reflected across `a` (no ids / bindings — see mirrorSelection). */
export function mirrorGeometry(el: MEl, a: Axis): Mirrored {
  const phi = axisAngle(a);
  if (LINEAR.has(el.type)) {
    const pts = linearPoints(el).map((p) => reflectPoint(p, a));
    const [x0, y0] = pts[0];
    const rel = pts.map(([x, y]) => [x - x0, y - y0] as Pt);
    const xs = rel.map((p) => p[0]);
    const ys = rel.map((p) => p[1]);
    const patch: Partial<MEl> = {
      x: x0,
      y: y0,
      angle: 0,
      points: rel,
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
    if (el.lastCommittedPoint) patch.lastCommittedPoint = null;
    // An elbow arrow stays orthogonal only when the axis is horizontal or vertical.
    if (el.elbowed) {
      const axisAligned = Math.abs(Math.sin(2 * phi)) < 1e-9;
      if (!axisAligned) {
        patch.elbowed = false;
        patch.fixedSegments = null;
      }
    }
    return { patch, fixed: identityFixed };
  }
  const c: Pt = [el.x + el.width / 2, el.y + el.height / 2];
  const c2 = reflectPoint(c, a);
  const m = mirrorAngle(el.angle ?? 0, phi);
  const readable = READABLE.has(el.type);
  const angle = readable ? m.angle : m.raw;
  const rot180 = readable && m.rot180;
  const patch: Partial<MEl> = { x: c2[0] - el.width / 2, y: c2[1] - el.height / 2, angle: toStored(angle) };
  if (el.type === 'image') {
    const [sx, sy] = (el.scale as [number, number] | undefined) ?? [1, 1];
    // M_φ ∘ Rot(θ) ∘ S(sx, sy) = Rot(2φ−θ) ∘ S(sx, −sy) = Rot(2φ−θ−π) ∘ S(−sx, sy)
    patch.scale = rot180 ? [-sx, sy] : [sx, -sy];
  }
  // Local frame: base reflection flips y; the readable half turn then flips both, i.e. net flip of x.
  const fixed: FixedMap = rot180 ? (fp) => [1 - fp[0], fp[1]] : (fp) => [fp[0], 1 - fp[1]];
  return { patch, fixed };
}

const rnd = () => Math.floor(Math.random() * 2 ** 31);
const randomId = () => Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 8);

/** The selection plus what travels with it: bound text, frame children, labels of arrows. */
export function expandSelection(scene: readonly MEl[], ids: readonly string[]): Set<string> {
  const live = scene.filter((e) => !e.isDeleted);
  const byId = new Map(live.map((e) => [e.id, e]));
  const out = new Set<string>(ids.filter((id) => byId.has(id)));
  // A selected frame takes its children.
  for (const id of [...out]) {
    if (byId.get(id)?.type === 'frame' || byId.get(id)?.type === 'magicframe') {
      for (const e of live) if (e.frameId === id) out.add(e.id);
    }
  }
  // Bound text goes with its container (and a selected bound text brings its container).
  for (const id of [...out]) {
    const e = byId.get(id)!;
    for (const b of e.boundElements ?? []) if (b.type === 'text' && byId.has(b.id)) out.add(b.id);
    if (e.containerId && byId.has(e.containerId)) out.add(e.containerId);
  }
  return out;
}

/** End point of an arrow (scene space). */
const endOf = (el: MEl, which: 'start' | 'end'): Pt => {
  const pts = linearPoints(el);
  return which === 'start' ? pts[0] : pts[pts.length - 1];
};
/** Does `pt` touch `el`'s box (expanded by `pad`)? Rotation-aware via the outline's bounding box. */
const touches = (el: MEl, pt: Pt, pad: number) => {
  const { pts } = outlineOf(el);
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return pt[0] >= Math.min(...xs) - pad && pt[0] <= Math.max(...xs) + pad && pt[1] >= Math.min(...ys) - pad && pt[1] <= Math.max(...ys) + pad;
};

export interface MirrorResult {
  /** Elements to add (duplicate mode). */
  added: MEl[];
  /** Replacements for existing elements (in-place mode, plus outside elements whose bindings changed). */
  changed: MEl[];
  /** Ids to select afterwards. */
  select: string[];
}

export interface MirrorOptions {
  duplicate: boolean;
  now?: number;
  newId?: () => string;
}

/**
 * Mirror `ids` (expanded with bound text / frame children) across `axis`.
 * `scene` should include every element (deleted ones are ignored).
 */
export function mirrorSelection(scene: readonly MEl[], ids: readonly string[], axis: Axis, opts: MirrorOptions): MirrorResult {
  const now = opts.now ?? Date.now();
  const newId = opts.newId ?? randomId;
  const live = scene.filter((e) => !e.isDeleted);
  const byId = new Map(live.map((e) => [e.id, e]));
  const set = expandSelection(scene, ids);
  const members = live.filter((e) => set.has(e.id));
  if (!members.length) return { added: [], changed: [], select: [] };

  const idMap = new Map<string, string>();
  for (const e of members) idMap.set(e.id, opts.duplicate ? newId() : e.id);
  const groupMap = new Map<string, string>();
  const mapGroup = (g: string) => {
    if (!opts.duplicate) return g;
    if (!groupMap.has(g)) groupMap.set(g, newId());
    return groupMap.get(g)!;
  };

  const geo = new Map<string, Mirrored>();
  for (const e of members) geo.set(e.id, mirrorGeometry(e, axis));

  // Bound text follows its container: same centre, container's (readable) angle.
  const containerAngle = (e: MEl) => {
    const c = e.containerId ? byId.get(e.containerId) : undefined;
    if (!c || !set.has(c.id)) return null;
    return LINEAR.has(c.type) ? 0 : (geo.get(c.id)!.patch.angle as number);
  };

  const result: MirrorResult = { added: [], changed: [], select: [] };
  const outsideChanges = new Map<string, MEl>(); // outside elements whose boundElements change
  const outside = (id: string) => outsideChanges.get(id) ?? byId.get(id);

  let fixtureN = 0;
  for (const e of members) {
    const g = geo.get(e.id)!;
    const next: MEl = { ...e, ...g.patch };
    if (e.type === 'text' && e.containerId) {
      const ang = containerAngle(e);
      if (ang !== null) next.angle = ang;
    }
    next.id = idMap.get(e.id)!;
    next.groupIds = (e.groupIds ?? []).map(mapGroup);
    if (e.containerId) next.containerId = set.has(e.containerId) ? idMap.get(e.containerId)! : e.containerId;
    if (e.frameId) {
      if (set.has(e.frameId)) next.frameId = idMap.get(e.frameId)!;
      else if (opts.duplicate) {
        // A copy stays in its original frame only if it still sits inside it.
        const f = byId.get(e.frameId);
        const c: Pt = [next.x + next.width / 2, next.y + next.height / 2];
        next.frameId = f && c[0] >= f.x && c[0] <= f.x + f.width && c[1] >= f.y && c[1] <= f.y + f.height ? e.frameId : null;
      }
    }
    // boundElements: keep members (remapped); outside arrows bound to this element are handled below.
    if (e.boundElements) {
      next.boundElements = e.boundElements
        .filter((b: { id: string }) => set.has(b.id))
        .map((b: { id: string; type: string }) => ({ ...b, id: idMap.get(b.id)! }));
      if (!opts.duplicate) {
        // In place: an outside arrow keeps its binding to this element only if its end still touches it.
        for (const b of e.boundElements as { id: string; type: string }[]) {
          if (set.has(b.id) || b.type !== 'arrow') continue;
          const arrow = outside(b.id);
          if (!arrow) continue;
          const which = arrow.startBinding?.elementId === e.id ? 'start' : arrow.endBinding?.elementId === e.id ? 'end' : null;
          if (!which) continue;
          const gap = (which === 'start' ? arrow.startBinding?.gap : arrow.endBinding?.gap) ?? 0;
          if (touches(next, endOf(arrow, which), gap + 4)) next.boundElements.push(b);
          else outsideChanges.set(arrow.id, { ...arrow, [which === 'start' ? 'startBinding' : 'endBinding']: null });
        }
      }
      if (!next.boundElements.length) next.boundElements = null;
    }
    // Arrow bindings.
    for (const key of ['startBinding', 'endBinding'] as const) {
      const b = e[key];
      if (!b) continue;
      if (set.has(b.elementId)) {
        const tg = geo.get(b.elementId)!;
        next[key] = {
          ...b,
          elementId: idMap.get(b.elementId)!,
          ...(typeof b.focus === 'number' ? { focus: -b.focus } : {}),
          ...(Array.isArray(b.fixedPoint) && next.elbowed ? { fixedPoint: tg.fixed(b.fixedPoint as [number, number]) } : {}),
        };
        if (!next.elbowed && 'fixedPoint' in next[key]) next[key] = { ...next[key], fixedPoint: null };
      } else {
        const target = outside(b.elementId);
        const still = target && touches(target, endOf(next, key === 'startBinding' ? 'start' : 'end'), (b.gap ?? 0) + 4);
        if (still && opts.duplicate) {
          next[key] = b; // the copy also touches it: bind the copy too
          const t = outside(b.elementId)!;
          outsideChanges.set(t.id, { ...t, boundElements: [...(t.boundElements ?? []), { id: next.id, type: 'arrow' }] });
        } else if (!still) {
          next[key] = null;
          if (!opts.duplicate && target) {
            outsideChanges.set(target.id, { ...target, boundElements: (target.boundElements ?? []).filter((x: { id: string }) => x.id !== e.id) });
          }
        }
      }
    }
    // Copies count as new things: fixtures get their own placement time (numbering), frames a new name.
    const est = (e.customData as { estimate?: { role?: string; placed_at?: number } } | undefined)?.estimate;
    if (opts.duplicate) {
      next.seed = rnd();
      next.versionNonce = rnd();
      next.version = 1;
      next.updated = now;
      delete next.index;
      if (est && (est.role === 'fixture' || est.role === 'dimension')) {
        next.customData = { ...e.customData, estimate: { ...est, placed_at: now + fixtureN++ } };
      }
      if ((e.type === 'frame' || e.type === 'magicframe') && e.name) next.name = `${e.name} (mirrored)`;
      result.added.push(next);
    } else {
      next.version = (e.version ?? 1) + 1;
      next.versionNonce = rnd();
      next.updated = now;
      result.changed.push(next);
    }
    if (!e.containerId) result.select.push(next.id);
  }
  for (const v of outsideChanges.values()) {
    const base = byId.get(v.id)!;
    result.changed.push({ ...v, version: (base.version ?? 1) + 1, versionNonce: rnd(), updated: now });
  }
  return result;
}
